import React, { useState, useRef } from "react";
import { api } from "../../../services/api";
import '../CrmModals.css';

// File size limits in bytes
const MAX_FILE_SIZES = {
  image: 10 * 1024 * 1024,   // 10MB
  video: 50 * 1024 * 1024,   // 50MB
  audio: 20 * 1024 * 1024    // 20MB
};

// Accepted file types
const ACCEPTED_TYPES = {
  image: '.jpg,.jpeg,.png,.webp',
  video: '.mp4',
  audio: '.mp3,.ogg,.wav,.m4a,.opus'
};

// Get all accepted types as a single string
const ALL_ACCEPTED_TYPES = Object.values(ACCEPTED_TYPES).join(',');

// Detect media type from MIME type
const getMediaType = (mimeType) => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return null;
};

// Format bytes to human-readable
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Get max size for a file type
const getMaxSize = (mimeType) => {
  const mediaType = getMediaType(mimeType);
  return MAX_FILE_SIZES[mediaType] || MAX_FILE_SIZES.image;
};

/**
 * MediaUploader Component
 *
 * Props:
 * - onUpload: (mediaInfo) => void - Called when upload succeeds
 * - onRemove: () => void - Called when user removes media
 * - mediaInfo: object | null - Current media info (id, original_filename, media_type, etc.)
 * - disabled: boolean - Disable interactions
 * - campaignId: string - Optional campaign UUID
 */
const MediaUploader = ({
  onUpload,
  onRemove,
  mediaInfo = null,
  disabled = false,
  campaignId = null
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    setError(null);

    // Get media type
    const mediaType = getMediaType(file.type);
    if (!mediaType) {
      setError("Tipo de arquivo não suportado. Use: JPG, PNG, WebP, MP4, MP3, OGG, WAV, M4A, Opus");
      return;
    }

    // Validate file size
    const maxSize = getMaxSize(file.type);
    if (file.size > maxSize) {
      setError(`Arquivo muito grande. Máximo para ${mediaType}: ${formatBytes(maxSize)}`);
      return;
    }

    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (campaignId) {
        formData.append('campaign_id', campaignId);
      }

      const response = await api.post('/api/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });


      if (response.data.success && response.data.media) {
        onUpload(response.data.media);
        setError(null);
      } else {
        setError(response.data.error || 'Erro no upload');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Erro ao enviar arquivo');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    if (disabled || uploading) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleClick = () => {
    if (!disabled && !uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (!disabled && onRemove) {
      onRemove();
    }
  };

  // Render media preview based on type
  const renderPreview = () => {
    if (!mediaInfo) return null;

    const { media_type, original_filename, file_size, preview_url } = mediaInfo;

    return (
      <div className="media-uploader-preview">
        {media_type === 'image' && preview_url && (
          <div className="media-uploader-image-container">
            <img
              src={preview_url}
              alt={original_filename}
              className="media-uploader-image"
            />
          </div>
        )}

        {media_type === 'video' && (
          <div className="media-uploader-file-info">
            <span className="media-uploader-icon">&#127909;</span>
            <div className="media-uploader-details">
              <span className="media-uploader-filename">{original_filename}</span>
              <span className="media-uploader-size">{formatBytes(file_size)}</span>
            </div>
          </div>
        )}

        {media_type === 'audio' && (
          <div className="media-uploader-file-info">
            <span className="media-uploader-icon">&#127911;</span>
            <div className="media-uploader-details">
              <span className="media-uploader-filename">{original_filename}</span>
              <span className="media-uploader-size">{formatBytes(file_size)}</span>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          className="media-uploader-remove-btn"
          title="Remover mídia"
        >
          &times;
        </button>
      </div>
    );
  };

  return (
    <div className="media-uploader">
      {!mediaInfo ? (
        <div
          className={`media-uploader-dropzone ${dragOver ? 'drag-over' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {uploading ? (
            <div className="media-uploader-loading">
              <div className="crm-spinner" />
              <span>Enviando...</span>
            </div>
          ) : (
            <div className="media-uploader-placeholder">
              <span className="media-uploader-icon">&#128206;</span>
              <span className="media-uploader-text">Clique ou arraste para anexar mídia</span>
              <span className="media-uploader-hint">Imagem, vídeo ou áudio</span>
            </div>
          )}
        </div>
      ) : (
        renderPreview()
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALL_ACCEPTED_TYPES}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled || uploading}
      />

      {error && (
        <div className="media-uploader-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
